import apiClient from '../request/http-common';

class P2PPartnerService {
  constructor() {
    this.apiClient = apiClient;
  }

  async handleResponse(response) {
    return response.data;
  }

  // Partner application
  async applyForPartnership(applicationData) {
    const response = await this.apiClient.post('/api/p2p-partner/apply', applicationData);
    return this.handleResponse(response);
  }

  // Profile management
  async getProfile() {
    const response = await this.apiClient.get('/api/p2p-partner/profile');
    return this.handleResponse(response);
  }

  async updateProfile(profileData) {
    const response = await this.apiClient.put('/api/p2p-partner/profile', profileData);
    return this.handleResponse(response);
  }

  // Rate management
  async getRates() {
    const response = await this.apiClient.get('/api/p2p-partner/rates');
    return this.handleResponse(response);
  }

  async createRate(rateData) {
    const response = await this.apiClient.post('/api/p2p-partner/rates', rateData);
    return this.handleResponse(response);
  }

  async updateRate(rateId, rateData) {
    const response = await this.apiClient.put(`/api/p2p-partner/rates/${rateId}`, rateData);
    return this.handleResponse(response);
  }

  async deleteRate(rateId) {
    const response = await this.apiClient.delete(`/api/p2p-partner/rates/${rateId}`);
    return this.handleResponse(response);
  }

  // Order management
  async getOrders(status = '', page = 1) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (page) params.append('page', page.toString());
    
    const response = await this.apiClient.get(`/api/p2p-partner/orders?${params}`);
    return this.handleResponse(response);
  }

  async acceptOrder(orderId) {
    const response = await this.apiClient.post(`/api/p2p-partner/orders/${orderId}/accept`);
    return this.handleResponse(response);
  }

  async confirmPayment(orderId) {
    const response = await this.apiClient.post(`/api/p2p-partner/orders/${orderId}/confirm-payment`);
    return this.handleResponse(response);
  }

  async cancelOrder(orderId, reason) {
    const response = await this.apiClient.post(`/api/p2p-partner/orders/${orderId}/cancel`, { reason });
    return this.handleResponse(response);
  }

  async completeOrder(orderId, notes = '') {
    const response = await this.apiClient.post(`/api/p2p-partner/orders/${orderId}/complete`, { notes });
    return this.handleResponse(response);
  }

  async createDispute(orderId, reason) {
    const response = await this.apiClient.post(`/api/p2p-partner/orders/${orderId}/dispute`, { reason });
    return this.handleResponse(response);
  }

  // Statistics
  async getStatistics() {
    const response = await this.apiClient.get('/api/p2p-partner/statistics');
    return this.handleResponse(response);
  }

  // Public partner list (for customers)
  async getPublicPartners(filters = {}) {
    const params = new URLSearchParams();
    if (filters.country) params.append('country', filters.country);
    if (filters.currency) params.append('currency', filters.currency);
    
    const response = await this.apiClient.get(`/api/p2p-partners?${params}`);
    return this.handleResponse(response);
  }

  // Utility methods
  formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(amount);
  }

  formatDate(dateString) {
    return new Date(dateString).toLocaleString();
  }

  getStatusColor(status) {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  }

  getOrderStatusColor(status) {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'info';
      case 'payment_pending':
        return 'secondary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'disputed':
        return 'error';
      default:
        return 'default';
    }
  }
}

export default new P2PPartnerService();
